import React, { ReactNode } from 'react';
import moment from 'moment';
import SetDefaultButton from '../components/SetDefaultButton';
import {
    AutocompleteInput,
    BooleanField,
    DateField,
    DateInput,
    Filter,
    ReferenceInput,
    SelectInput,
    TextField,
    ShowButton,
} from 'react-admin';

import CountryField from '../components/CountryField';
import CustomSearchInput from '../components/CustomSearchInput';
import { SelectArrayInput } from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

const CCExpiry = ({ record = {}, source = 'expiryDate', label = '' }) => {
    const expiryDate = record[source];
    return (
        expiryDate && (
            <span>{`${expiryDate.substring(2, 4)} / ${expiryDate.substring(
                0,
                2
            )}`}</span>
        )
    );
};

const CardFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />
        <ReferenceInput
            source="partnerId"
            reference="partners"
            resource="partners"
            label="Partner"
        >
            <SelectInput />
        </ReferenceInput>

        <ReferenceInput
            source="userId"
            reference="users"
            resource="users"
            label="Customer"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={user => (user.name ? user.name : user.email)}
            />
        </ReferenceInput>

        <SelectArrayInput
            style={{ minWidth: '10rem' }}
            source="status"
            choices={[
                { id: 'active', name: 'active' },
                { id: 'inactive', name: 'inactive' },
            ]}
        />

        <DateInput
            source="createdAt[$gte]"
            label="Created From"
            parse={v => v && moment(v).startOf('day').format()}
        />
        <DateInput
            source="createdAt[$lte]"
            label="Created To"
            parse={v => v && moment(v).endOf('day').format()}
        />
    </Filter>
);

const CardList = props => {
    const permissions = props.permissions || [];

    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'createdAt', label: 'Created', visible: true, order: 2 },
        { id: 'country', label: 'Country', visible: true, order: 3 },
        { id: 'customer', label: 'Customer', visible: true, order: 4 },
        { id: 'cardHolderName', label: 'Name', visible: true, order: 5 },
        { id: 'cardNumber', label: 'Card', visible: true, order: 6 },
        { id: 'expiryDate', label: 'Expiry', visible: true, order: 7 },
        { id: 'status', label: 'Status', visible: true, order: 8 },
        { id: 'default', label: 'Default', visible: true, order: 9 },
        { id: 'show', label: 'Show', visible: true, order: 10 },
        { id: 'setDefault', label: 'Set Default', visible: true, order: 11 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="createdAt"
                        label="Created"
                        showTime
                    />
                );
            case 'country':
                return (
                    <TextField
                        key={columnId}
                        label="Country"
                        source="country.name"
                        sortable={false}
                    />
                );
            case 'customer':
                return (
                    <TextField
                        key={columnId}
                        label="Customer"
                        source="user.name"
                        sortable={false}
                        onClick={(e, record) => {
                            e.stopPropagation();
                            if (record && record.userId) {
                                window.location.href = `/#/users/${record.userId}/show`;
                            }
                        }}
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: '#2196f3',
                        }}
                    />
                );
            case 'cardHolderName':
                return (
                    <TextField
                        key={columnId}
                        source="cardHolderName"
                        label="Name"
                    />
                );
            case 'cardNumber':
                return (
                    <TextField
                        key={columnId}
                        source="cardNumber"
                        label="Card"
                    />
                );
            case 'expiryDate':
                return (
                    <CCExpiry
                        key={columnId}
                        source="expiryDate"
                        label="Expiry"
                    />
                );
            case 'status':
                return <TextField key={columnId} source="status" />;
            case 'default':
                return <BooleanField key={columnId} source="default" />;
            case 'show':
                return <ShowButton key={columnId} />;
            case 'setDefault':
                return <SetDefaultButton key={columnId} undoable={false} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Cards',
        permissions,
        defaultColumns,
        storageKey: 'cardListColumns',
        renderColumn,
        bulkActionButtons: false,
        filtersComponent: <CardFilter />,
        sort: { field: 'id', order: 'DESC' },
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'cards',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

CardList.defaultProps = {
    resource: 'cards',
    location: { search: '' },
};

export default CardList;
